import React, { useRef, useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import { cn } from "../../utils/cn";
import { getPlatformConfig } from "../../utils/platformIcons";
import { getPostImageUrl } from "../../shared/constants";
import AIOptimizerMenu from "../features/AIOptimizerMenu";
import CommentsList from "../features/CommentsList";
import Button from "../ui/Button";
import Tooltip from "../ui/Tooltip";
import type PostCardComponent from "../cards/PostCard";
import {
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Edit3,
  Calendar,
  CalendarPlus,
  Send,
  RefreshCw,
  Image as ImageIcon,
  Video as VideoIcon,
  Clock,
  Trash2,
  ThumbsUp,
  MessageCircle,
  ExternalLink,
  Loader2,
  AlertTriangle,
  DollarSign,
  Linkedin,
  Share2,
} from "lucide-react";

type PostTableRowProps = React.ComponentProps<typeof PostCardComponent> & {
  showEngagementColumn?: boolean;
};

const PostTableRow: React.FC<PostTableRowProps> = ({
  post,
  postMetrics,
  onApprove,
  onReject,
  onRevertToPending,
  onEdit,
  onPreview,
  onSchedule,
  onUnschedule,
  onDelete,
  onPublish,
  onPublishMulti,
  onRepublish,
  onReschedule,
  onBoostMetaAds,
  onBoostLinkedInAds,
  onSyncMetrics,
  loadingStates,
  highlighted,
  innerRef,
  showEngagementColumn = true,
  children, // AI Action Bar / Comments List
}) => {
  const { t } = useTranslation();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu on click outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };
    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  const platformConfig = getPlatformConfig(post.platform || "unknown");
  const PlatformIcon = platformConfig.icon;

  // "Otimizar com IA" e "Comentários" ganham colunas próprias (mais previsível
  // para varrer a tabela); demais children seguem inline no post.
  const childrenArray = React.Children.toArray(children);
  const aiOptimizerNode = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === AIOptimizerMenu,
  );
  const commentsNode = childrenArray.find(
    (child) => React.isValidElement(child) && child.type === CommentsList,
  );
  const otherChildren = childrenArray.filter(
    (child) => child !== aiOptimizerNode && child !== commentsNode,
  );

  const truncateText = (text: string, length: number) => {
    if (!text) return "";
    return text.length > length ? text.substring(0, length) + "..." : text;
  };

  const getMediaThumbnail = () => {
    let imageUrl = "";
    let isVideo = false;

    if (post.media_gallery && post.media_gallery.length > 0) {
      imageUrl =
        post.media_gallery[0].public_url || post.media_gallery[0].url || "";
      isVideo = post.media_gallery[0].type === "video";
    } else if (
      post.image_info &&
      (post.image_info.filename || post.image_info.public_url)
    ) {
      imageUrl = getPostImageUrl(post.image_info);
    } else if (
      post.video_info &&
      (post.video_info.filename || post.video_info.public_url)
    ) {
      isVideo = true;
    }

    if (imageUrl) {
      return (
        <div className="relative w-12 h-12 shrink-0 rounded-lg overflow-hidden border border-gray-200">
          <img
            src={imageUrl}
            alt={t("Miniatura")}
            className="w-full h-full object-cover"
          />
          {isVideo && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <VideoIcon className="h-4 w-4 text-white" />
            </div>
          )}
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="w-12 h-12 shrink-0 bg-indigo-50 rounded-lg flex items-center justify-center border border-indigo-100">
          <VideoIcon className="h-5 w-5 text-indigo-400" />
        </div>
      );
    }

    return (
      <div className="w-12 h-12 shrink-0 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200">
        <ImageIcon className="h-5 w-5 text-gray-300" />
      </div>
    );
  };

  // Mesmo esquema de cor/ícone por status usado no PostCard (visão em cards).
  // Cores por status ainda divergem (badge sólido aqui vs. ponto+texto no card);
  // labels e ícones são mantidos em paridade.
  const getStatusBadge = () => {
    const config: Record<
      string,
      { label: string; icon: React.ReactNode; className: string }
    > = {
      pending: {
        label: t("Aguardando aprovação"),
        icon: <Clock className="h-3 w-3 mr-1" />,
        className: "bg-amber-100 text-amber-800",
      },
      approved: {
        label: t("Aprovado"),
        icon: <CheckCircle className="h-3 w-3 mr-1" />,
        className: "bg-emerald-100 text-emerald-800",
      },
      scheduled: {
        label: t("Agendado"),
        icon: <CalendarPlus className="h-3 w-3 mr-1" />,
        className: "bg-blue-100 text-blue-800",
      },
      published: {
        label: t("Publicado"),
        icon: <Send className="h-3 w-3 mr-1" />,
        className: "bg-purple-100 text-purple-800",
      },
      rejected: {
        label: t("Rejeitado"),
        icon: <XCircle className="h-3 w-3 mr-1" />,
        className: "bg-red-50 text-red-500",
      },
      failed: {
        label: t("Falha ao publicar"),
        icon: <AlertTriangle className="h-3 w-3 mr-1" />,
        className: "bg-red-100 text-red-600",
      },
    };

    const entry = config[post.status];
    if (!entry) {
      return (
        <span className="inline-flex h-9 items-center whitespace-nowrap rounded-full px-3 text-xs font-semibold bg-gray-100 text-gray-500">
          {post.status}
        </span>
      );
    }
    return (
      <span
        className={cn(
          "inline-flex h-9 items-center whitespace-nowrap rounded-full px-3 text-xs font-semibold",
          entry.className,
        )}
      >
        {entry.icon}
        {entry.label}
      </span>
    );
  };

  return (
    <tr
      ref={innerRef as unknown as React.Ref<HTMLTableRowElement>}
      className={cn(
        "group/row transition-colors border-b border-gray-100 hover:bg-gray-50/50",
        highlighted && "bg-purple-50/50",
      )}
    >
      {/* 1. Mídia e Conteúdo */}
      <td className="px-4 py-3 align-top min-w-[280px]">
        <div className="flex items-center gap-3">
          {getMediaThumbnail()}
          <div className="flex-1 min-w-0">
            <p
              className="text-sm text-gray-700 font-medium line-clamp-2 leading-snug cursor-pointer hover:text-purple-600 transition-colors"
              onClick={onPreview}
            >
              {truncateText(post.content, 120) || (
                <span className="italic text-gray-400">
                  {t("Sem conteúdo")}
                </span>
              )}
            </p>
            {post.scheduled_date && (
              <p className="text-xs text-purple-600 mt-1 flex items-center gap-1 font-medium">
                <Calendar className="h-3 w-3" />{" "}
                {new Date(post.scheduled_date).toLocaleString()}
              </p>
            )}
            {post.status === "rejected" &&
              (post.rejection_reason || post.failure_reason) && (
                <Tooltip
                  content={post.rejection_reason || post.failure_reason || ""}
                >
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1 font-medium truncate max-w-[220px]">
                    <AlertTriangle className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {post.rejection_reason || post.failure_reason}
                    </span>
                  </p>
                </Tooltip>
              )}
          </div>
        </div>
        {otherChildren.length > 0 && (
          <div className="mt-2 pl-[60px] flex flex-col gap-2">
            {otherChildren}
          </div>
        )}
      </td>

      {/* 2. Plataforma */}
      <td className="px-4 py-3 align-middle w-[80px]">
        <Tooltip content={platformConfig.name}>
          <div
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center",
              platformConfig.bgColor,
              platformConfig.color,
            )}
          >
            <PlatformIcon className="h-4 w-4" />
          </div>
        </Tooltip>
      </td>

      {/* 3. Status */}
      <td className="px-4 py-3 align-middle w-[210px]">{getStatusBadge()}</td>

      {/* 4. Métricas */}
      {showEngagementColumn && (
        <td className="px-4 py-3 align-middle w-[150px]">
          {post.status === "published" && postMetrics ? (
            <div className="flex items-center gap-3 text-xs text-gray-600">
              <div className="flex items-center gap-1" title={t("Curtidas")}>
                <ThumbsUp className="h-3 w-3 text-blue-500" />{" "}
                {postMetrics.likes || 0}
              </div>
              <div className="flex items-center gap-1" title={t("Comentários")}>
                <MessageCircle className="h-3 w-3 text-emerald-500" />{" "}
                {postMetrics.comments || 0}
              </div>
            </div>
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </td>
      )}

      {/* 5. Otimizar com IA */}
      {aiOptimizerNode && (
        <td className="px-4 py-3 align-middle w-[230px]">{aiOptimizerNode}</td>
      )}

      {/* 6. Comentários */}
      {commentsNode && (
        <td className="px-4 py-3 align-middle w-[200px]">{commentsNode}</td>
      )}

      {/* 7. Ações */}
      <td className="px-4 py-3 align-middle text-right w-[80px]">
        <div className="relative inline-block text-left" ref={menuRef}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowMenu(!showMenu)}
            className="h-9 w-9 p-0 rounded-full"
          >
            <MoreHorizontal className="h-4 w-4 text-gray-500" />
          </Button>

          {showMenu && (
            <div className="absolute right-0 mt-1 w-56 bg-white rounded-xl shadow-xl shadow-purple-500/10 border border-purple-100 z-50 py-1 flex flex-col overflow-hidden">
              {onPreview && (
                <button
                  onClick={() => {
                    onPreview();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4 text-gray-400" />{" "}
                  {t("Visualizar Prévia")}
                </button>
              )}

              {onEdit && (
                <button
                  onClick={() => {
                    onEdit();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  {loadingStates?.editing ? (
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                  ) : (
                    <Edit3 className="h-4 w-4 text-gray-400" />
                  )}{" "}
                  {t("Editar Post")}
                </button>
              )}

              {onApprove && post.status === "pending" && (
                <button
                  onClick={() => {
                    onApprove();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-green-700 hover:bg-green-50 flex items-center gap-2"
                >
                  {loadingStates?.approving ? (
                    <Loader2 className="h-4 w-4 animate-spin text-green-600" />
                  ) : (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  )}{" "}
                  {t("Aprovar")}
                </button>
              )}

              {onReject && post.status === "pending" && (
                <button
                  onClick={() => {
                    onReject();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-orange-700 hover:bg-orange-50 flex items-center gap-2"
                >
                  {loadingStates?.rejecting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-orange-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-orange-500" />
                  )}{" "}
                  {t("Rejeitar")}
                </button>
              )}

              {onRevertToPending && post.status === "approved" && (
                <button
                  onClick={() => {
                    onRevertToPending();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-orange-700 hover:bg-orange-50 flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4 text-orange-500" />{" "}
                  {t("Reverter para Pendente")}
                </button>
              )}

              {onSchedule &&
                (post.status === "approved" || post.status === "scheduled") && (
                  <button
                    onClick={() => {
                      onSchedule();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-indigo-700 hover:bg-indigo-50 flex items-center gap-2"
                  >
                    {loadingStates?.scheduling ? (
                      <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
                    ) : (
                      <Calendar className="h-4 w-4 text-indigo-500" />
                    )}{" "}
                    {t("Agendar")}
                  </button>
                )}

              {onUnschedule && post.status === "scheduled" && (
                <button
                  onClick={() => {
                    onUnschedule();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-orange-700 hover:bg-orange-50 flex items-center gap-2"
                >
                  <XCircle className="h-4 w-4 text-orange-500" />{" "}
                  {t("Remover Agendamento")}
                </button>
              )}

              {onPublish &&
                (post.status === "approved" || post.status === "scheduled") &&
                !post.published_at && (
                  <button
                    onClick={() => {
                      onPublish();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-blue-700 hover:bg-blue-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    {loadingStates?.publishing ? (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    ) : (
                      <Send className="h-4 w-4 text-blue-500" />
                    )}{" "}
                    {t("Publicar Agora")}
                  </button>
                )}

              {onPublishMulti &&
                (post.status === "approved" || post.status === "scheduled") && (
                  <button
                    onClick={() => {
                      onPublishMulti();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                  >
                    <Share2 className="h-4 w-4 text-purple-500" />{" "}
                    {t("Multi Plataforma")}
                  </button>
                )}

              {onRepublish && post.status === "published" && (
                <button
                  onClick={() => {
                    onRepublish();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  {loadingStates?.republishing ? (
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600" />
                  ) : (
                    <RefreshCw className="h-4 w-4 text-purple-500" />
                  )}{" "}
                  {t("Republicar")}
                </button>
              )}

              {onReschedule && post.status === "published" && (
                <button
                  onClick={() => {
                    onReschedule();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-purple-700 hover:bg-purple-50 flex items-center gap-2"
                >
                  <CalendarPlus className="h-4 w-4 text-purple-500" />{" "}
                  {t("Reagendar")}
                </button>
              )}

              {onBoostMetaAds &&
                (post.status === "approved" ||
                  post.status === "scheduled" ||
                  post.status === "published") && (
                  <button
                    onClick={() => {
                      onBoostMetaAds();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <DollarSign className="h-4 w-4 text-gray-400" />{" "}
                    {t("Turbinar com Meta Ads")}
                  </button>
                )}

              {onBoostLinkedInAds &&
                (post.status === "approved" ||
                  post.status === "scheduled" ||
                  post.status === "published") && (
                  <button
                    onClick={() => {
                      onBoostLinkedInAds();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Linkedin className="h-4 w-4 text-gray-400" />{" "}
                    {t("Patrocinar no LinkedIn")}
                  </button>
                )}

              {onSyncMetrics &&
                post.status === "published" &&
                post.platform !== "google_my_business" && (
                  <button
                    onClick={() => {
                      onSyncMetrics();
                      setShowMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 border-t border-gray-100"
                  >
                    <RefreshCw className="h-4 w-4 text-gray-400" />{" "}
                    {t("Sincronizar Métricas")}
                  </button>
                )}

              {onDelete && post.status !== "scheduled" && (
                <button
                  onClick={() => {
                    onDelete();
                    setShowMenu(false);
                  }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 border-t border-red-100/50"
                >
                  {loadingStates?.deleting ? (
                    <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-red-500" />
                  )}{" "}
                  {t("Excluir")}
                </button>
              )}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

export default PostTableRow;
